
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Download, Share } from 'lucide-react';

interface ReferralQRCodeProps {
  referralLink: string;
}

const ReferralQRCode: React.FC<ReferralQRCodeProps> = ({ referralLink }) => {
  const { toast } = useToast();
  const [qrColor, setQrColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  
  // Download QR code as PNG
  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-canvas') as HTMLCanvasElement;
    if (canvas) {
      const pngUrl = canvas
        .toDataURL("image/png")
        .replace("image/png", "image/octet-stream");
      
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = "referral-qrcode.png";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      toast({
        title: "QR Code Downloaded",
        description: "Your QR code has been downloaded successfully.",
      });
    }
  };
  
  // Share QR code if Web Share API is available
  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Referral QR Code',
          text: 'Scan this QR code to use my referral link!',
          url: referralLink,
        });
        toast({
          title: "Shared Successfully",
          description: "Your referral link has been shared.",
        });
      } catch (error) {
        toast({
          title: "Sharing Failed",
          description: "Could not share the QR code.",
          variant: "destructive",
        });
      }
    } else {
      navigator.clipboard.writeText(referralLink);
      toast({
        title: "Link Copied",
        description: "Referral link copied to clipboard.",
      });
    }
  };
  
  // Color options for QR code
  const colorOptions = [
    { color: "#000000", bg: "#ffffff", name: "Classic" },
    { color: "#1e40af", bg: "#eff6ff", name: "Blue" },
    { color: "#047857", bg: "#ecfdf5", name: "Green" },
    { color: "#7c3aed", bg: "#f5f3ff", name: "Purple" },
    { color: "#b91c1c", bg: "#fef2f2", name: "Red" }
  ];
  
  return (
    <div className="flex flex-col items-center">
      <div className="mb-4 p-3 bg-white rounded-xl shadow-sm flex justify-center border border-gray-100">
        <QRCodeSVG 
          id="qr-canvas"
          value={referralLink}
          size={160}
          bgColor={bgColor}
          fgColor={qrColor}
          level={"H"}
          includeMargin={true}
          imageSettings={{
            src: "/placeholder.svg",
            x: undefined,
            y: undefined,
            height: 24,
            width: 24,
            excavate: true,
          }}
        />
      </div>
      
      <div className="w-full flex flex-wrap gap-2 mb-4 justify-center">
        {colorOptions.map((option) => (
          <button
            key={option.name}
            onClick={() => {
              setQrColor(option.color);
              setBgColor(option.bg);
            }}
            className={`w-6 h-6 rounded-full border ${qrColor === option.color ? 'ring-2 ring-blue-500 ring-offset-2' : 'border-gray-300'}`}
            style={{ backgroundColor: option.color }}
            title={option.name}
          />
        ))}
      </div>
      
      <div className="flex gap-2 w-full">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={downloadQRCode}
        >
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={shareQRCode}
        >
          <Share className="h-4 w-4 mr-2" />
          Share
        </Button>
      </div>
    </div>
  );
};

export default ReferralQRCode;
